import { graphql, useStaticQuery } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { CsAsset } from 'types/contentStack';
import {
  AcceptCta,
  ButtonContainer,
  HiddenOneTrustButton,
  ManageCta,
  NoticeBackgroundImage,
  NoticeBody,
  NoticeContent,
  NoticeHeading,
  VideoCookieNoticeContainer,
  VideoNotAvailableText,
} from './styles';

export type CsVideoCookiePolicyNotice = {
  csVideoCookiePolicyNotice: {
    heading: string;
    body: string;
    manage_cookies_button_text: string;
    reject_all_button_text: string;
    allow_all_button_text: string;
    background_image: CsAsset | null;
  };
};

const query = graphql`
  query {
    csVideoCookiePolicyNotice {
      heading
      body
      manage_cookies_button_text
      reject_all_button_text
      allow_all_button_text
      background_image {
        ...Asset
      }
    }
  }
`;

const VideoCookieNotice: React.FC = () => {
  const {
    csVideoCookiePolicyNotice: {
      heading,
      body,
      manage_cookies_button_text,
      allow_all_button_text,
      background_image,
    },
  } = useStaticQuery<CsVideoCookiePolicyNotice>(query);

  const [shouldShowCookieNotice, setShouldShowCookieNotice] = useState<boolean>(false);

  // When the user changes their consent of cookie categories (for example, reject marketing
  // cookies), the OneTrust banner script will be executed. This will in turn run the
  // OptanonWrapper function, where we are dispatching a "oneTrustBannerScriptExecuted" event
  useEffect(() => {
    const handleOneTrustEvent = (): void => {
      if (typeof window?.OnetrustActiveGroups !== 'undefined') {
        const marketingCookiesRejected = !window.OnetrustActiveGroups.includes('C0005');
        setShouldShowCookieNotice(marketingCookiesRejected);
      }
    };

    window.addEventListener('oneTrustBannerScriptExecuted', handleOneTrustEvent);
    return () => {
      window.removeEventListener('oneTrustBannerScriptExecuted', handleOneTrustEvent);
    };
  }, []);

  // Reset interval every 400 ms until OneTrust is defined. Then check if the user
  // has rejected marketing cookies. If so is the case we re-load the page to ensure
  // the video iframe's src attr is changed to data-src, disabling and hiding video.
  useEffect(() => {
    const interval = setInterval(() => {
      if (window?.OneTrust) {
        window.OneTrust?.OnConsentChanged((e) => {
          const cookiesAccepted = e?.detail ?? [];
          if (!cookiesAccepted.includes('C0005')) {
            window.location.reload();
          }
        });
      }
    }, 400);

    return () => clearInterval(interval);
  }, []);

  // TECH DEBT - TODO: move temp hardcoded text to Contentstack
  const thisVideoIsNotAvailableText = 'This video is not available';

  const manageCta = {
    displayText: manage_cookies_button_text,
    screenReaderText: manage_cookies_button_text,
    openInNewTab: false,
    background: 'light',
    type: 'button',
  };

  const acceptCta = {
    displayText: allow_all_button_text,
    screenReaderText: allow_all_button_text,
    openInNewTab: false,
    background: 'dark',
    type: 'button',
    onClick: () => {
      window?.OneTrust?.UpdateConsent('Category', 'C0005:1');
    },
  };

  return (
    <VideoCookieNoticeContainer
      shouldShowCookieNotice={shouldShowCookieNotice}
      data-testid="cookie-notice-container">
      <NoticeBackgroundImage alt="altText" src={background_image?.localAsset.publicURL} />
      <VideoNotAvailableText>{thisVideoIsNotAvailableText}</VideoNotAvailableText>
      <NoticeContent>
        <div>
          <NoticeHeading>{heading}</NoticeHeading>
          <NoticeBody html={body} />
        </div>
        <ButtonContainer>
          <HiddenOneTrustButton
            className="ot-sdk-show-settings"
            aria-label="onetrust-manage-cookies"
          />
          <ManageCta cta={manageCta} />
          <AcceptCta cta={acceptCta} />
        </ButtonContainer>
      </NoticeContent>
    </VideoCookieNoticeContainer>
  );
};

export default VideoCookieNotice;
